import { useState } from 'react'

import { useSesion } from '@/modules/auth/useSesion'

import { NuevoCliente } from './NuevoCliente'
import { useBuscarClientes } from './useComercial'
import estilos from './BuscadorCliente.module.css'

/** Lo mínimo que hace falta saber de un cliente para trabajar con él. */
export type ClienteElegido = {
  id_tercero: number
  nombre: string
  identificacion: string
  /** Para buscar la dirección en su municipio (0277). */
  ciudad?: string | null
  departamento?: string | null
}

type PropsBuscador = {
  etiqueta?: string
  elegido: ClienteElegido | null
  onElegir: (cliente: ClienteElegido | null) => void
  obligatorio?: boolean
  ayuda?: string
}

/**
 * Buscar un cliente, y crearlo si no está.
 *
 * Reemplaza al desplegable: con 2.566 terceros, una lista desplegable no se
 * usa, se padece. Y si el cliente no existe todavía, se crea aquí mismo — sin
 * eso, encontrarse con un cliente nuevo obligaba a abandonar el formulario,
 * ir a World Office y volver a empezar.
 */
export function BuscadorCliente({
  etiqueta = 'Cliente',
  elegido,
  onElegir,
  obligatorio = false,
  ayuda,
}: PropsBuscador) {
  const { puede } = useSesion()
  const [termino, setTermino] = useState('')
  const [creando, setCreando] = useState(false)

  const { clientes, cargando } = useBuscarClientes(termino)
  const buscando = termino.trim().length >= 2

  // El operario ya puede crear clientes (0157 le dio maestros.crear), así que
  // el técnico ve el botón igual que la oficina. Sigue siendo la misma regla
  // que el backend: fn_crear_cliente pide maestros.crear.
  const puedeCrear = puede('maestros', 'crear')

  // Ya hay uno escogido: se muestra y se deja cambiar.
  if (elegido) {
    return (
      <div>
        <span className={estilos.etiqueta}>{etiqueta}</span>

        <div className={estilos.elegido}>
          <div>
            <p className={estilos.elegidoNombre}>{elegido.nombre}</p>
            <p className={estilos.elegidoDocumento}>{elegido.identificacion}</p>
          </div>

          <button
            type="button"
            className={estilos.cambiar}
            onClick={() => {
              onElegir(null)
              setTermino('')
            }}
          >
            Cambiar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={estilos.zona}>
      <span className={estilos.etiqueta}>
        {etiqueta}
        {obligatorio && (
          <span className={estilos.obligatorio} aria-hidden="true">
            *
          </span>
        )}
      </span>

      <input
        className={estilos.campo}
        type="search"
        placeholder="Nombre o NIT…"
        value={termino}
        onChange={(e) => setTermino(e.target.value)}
        aria-label={`Buscar ${etiqueta.toLowerCase()}`}
      />

      {ayuda && !buscando && <p className={estilos.ayuda}>{ayuda}</p>}

      {buscando && (
        <div className={estilos.resultados}>
          {cargando && clientes.length === 0 ? (
            <p className={estilos.vacio}>Buscando…</p>
          ) : clientes.length === 0 ? (
            <p className={estilos.vacio}>Ningún cliente con ese nombre o documento.</p>
          ) : (
            clientes.map((cliente) => (
              <button
                key={cliente.id_tercero}
                type="button"
                className={estilos.opcion}
                onClick={() => {
                  onElegir({
                    id_tercero: cliente.id_tercero,
                    nombre: cliente.nombre,
                    identificacion: cliente.identificacion,
                    ciudad: cliente.ciudad ?? null,
                    departamento: cliente.departamento ?? null,
                  })
                  setTermino('')
                }}
              >
                <span className={estilos.opcionNombre}>{cliente.nombre}</span>
                <span className={estilos.opcionDocumento}>{cliente.identificacion}</span>
              </button>
            ))
          )}

          {puedeCrear && (
            <button type="button" className={estilos.crear} onClick={() => setCreando(true)}>
              + Crear «{termino.trim()}» en World Office
            </button>
          )}
        </div>
      )}

      <NuevoCliente
        abierto={creando}
        onCerrar={() => setCreando(false)}
        onCreado={(idTercero, nombre) => {
          // Recién creado: se escoge solo, para no obligar a buscarlo.
          onElegir({ id_tercero: idTercero, nombre, identificacion: termino.trim() })
          setTermino('')
          setCreando(false)
        }}
      />
    </div>
  )
}
